import { PHOTO_IMAGE } from "../constants"

class ImageElement {
  constructor(element) {
    this.element = element
    this.pictureElement = element.closest("picture")
    this._onLoadCallbackList = []
    this._isLoading = false

    this.element.addEventListener("load", () => {
      this._isLoading = false
      this._onLoadCallbackList.forEach((callback) => callback())
    })

    this.element.addEventListener("error", () => {
      this._isLoading = false
      this._onLoadCallbackList.forEach((callback) => callback())
    })
  }

  static fromHtmlElement(htmlElement) {
    const element = htmlElement.querySelector(PHOTO_IMAGE)
    return element ? new ImageElement(element) : null
  }

  onLoad(callback) {
    this._onLoadCallbackList.push(callback)
  }

  apply(photo) {
    if (!this.element) return
    if (this.element.dataset.photoId === String(photo.id)) return

    this._isLoading = true
    const previousPhotoId = this.element.dataset.photoId
    this.element.dataset.photoId = String(photo.id)
    if (photo.title) {
      this.element.alt = photo.title
    }

    // Always update image sources, even if some URLs might be missing
    // This ensures data-photo-id and image sources stay in sync
    const urls = photo.imageUrls || {}

    // Update WebP source element
    const webpSource = this.pictureElement?.querySelector('source[type="image/webp"]')
    if (webpSource) {
      const webpSrcset = []
      if (urls.medium_webp) webpSrcset.push(`${urls.medium_webp} 500w`)
      if (urls.large_webp) webpSrcset.push(`${urls.large_webp} 784w`)
      if (urls.lg1_webp) webpSrcset.push(`${urls.lg1_webp} 960w`)
      if (urls.lg2_webp) webpSrcset.push(`${urls.lg2_webp} 1200w`)
      if (urls.lg3_webp) webpSrcset.push(`${urls.lg3_webp} 1600w`)
      if (urls.lg4_webp) webpSrcset.push(`${urls.lg4_webp} 2400w`)
      if (urls.lg5_webp) webpSrcset.push(`${urls.lg5_webp} 3200w`)
      webpSource.srcset = webpSrcset.length > 0 ? webpSrcset.join(", ") : ""
    }

    // Update img srcset and src
    const imgSrcset = []
    if (urls.medium) imgSrcset.push(`${urls.medium} 500w`)
    if (urls.large) imgSrcset.push(`${urls.large} 784w`)
    if (urls.lg1) imgSrcset.push(`${urls.lg1} 960w`)
    if (urls.lg2) imgSrcset.push(`${urls.lg2} 1200w`)
    if (urls.lg3) imgSrcset.push(`${urls.lg3} 1600w`)
    if (urls.lg4) imgSrcset.push(`${urls.lg4} 2400w`)
    if (urls.lg5) imgSrcset.push(`${urls.lg5} 3200w`)

    if (imgSrcset.length > 0) {
      this.element.srcset = imgSrcset.join(", ")
      // Set src to lg3 as fallback, or first available size
      this.element.src =
        urls.lg3 || urls.lg2 || urls.lg1 || urls.large || urls.medium || photo.url || ""
    } else {
      // No responsive URLs available - clear srcset and use photo.url or empty string
      // This ensures broken image is shown if photo.url is also missing, rather than showing wrong photo
      this.element.srcset = ""
      this.element.src = photo.url || ""
    }

    // Check if image is already loaded (cached) - if so, trigger load callback
    // This handles the case where the browser uses a cached image
    if (this.element.complete && this.element.naturalHeight !== 0) {
      // Use setTimeout to ensure this runs after the current execution context
      setTimeout(() => {
        if (this._isLoading) {
          this._isLoading = false
          this._onLoadCallbackList.forEach((callback) => callback())
        }
      }, 0)
    }
  }
}

export default ImageElement
